/**
 * Utility to convert markdown content to ESC/POS commands
 */
import { textCenter, textLeft, textBold, textNormal, lineFeed, lineFeeds, cutPaper, textToUint8Array } from './escpos';

// Simple markdown parser for ESC/POS printing
export const markdownToEscPos = (markdown: string): Uint8Array => {
  const commands: number[] = [];
  
  // Initialize printer
  commands.push(0x1B, 0x40); // ESC @ - Initialize printer
  
  // Split content into lines
  const lines = markdown.split('\n');
  
  // Process each line
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Heading 1
    if (line.startsWith('# ')) {
      const centerCmd = textCenter();
      const boldCmd = textBold();
      const text = textToUint8Array(line.substring(2));
      const normalCmd = textNormal();
      const leftCmd = textLeft();
      const lfCmd = lineFeeds(2);
      
      addUint8ArrayToNumbers(commands, centerCmd);
      addUint8ArrayToNumbers(commands, boldCmd);
      addUint8ArrayToNumbers(commands, text);
      addUint8ArrayToNumbers(commands, normalCmd);
      addUint8ArrayToNumbers(commands, leftCmd);
      addUint8ArrayToNumbers(commands, lfCmd);
      continue;
    }
    
    // Heading 2
    if (line.startsWith('## ')) {
      const boldCmd = textBold();
      const text = textToUint8Array(line.substring(3));
      const normalCmd = textNormal();
      const lfCmd = lineFeed();
      
      addUint8ArrayToNumbers(commands, boldCmd);
      addUint8ArrayToNumbers(commands, text);
      addUint8ArrayToNumbers(commands, normalCmd);
      addUint8ArrayToNumbers(commands, lfCmd);
      continue;
    }
    
    // Horizontal rule
    if (line.startsWith('---')) {
      const text = textToUint8Array('--------------------------------');
      const lfCmd = lineFeed();
      
      addUint8ArrayToNumbers(commands, text);
      addUint8ArrayToNumbers(commands, lfCmd);
      continue;
    }
    
    // Bullet point
    if (line.startsWith('- ')) {
      const text = textToUint8Array('• ' + line.substring(2));
      const lfCmd = lineFeed();
      
      addUint8ArrayToNumbers(commands, text);
      addUint8ArrayToNumbers(commands, lfCmd);
      continue;
    }
    
    // Numbered list (simple detection)
    if (/^\d+\.\s/.test(line)) {
      const text = line.replace(/^\d+\.\s/, '');
      const textCmd = textToUint8Array('  ' + text);
      const lfCmd = lineFeed();
      
      addUint8ArrayToNumbers(commands, textCmd);
      addUint8ArrayToNumbers(commands, lfCmd);
      continue;
    }
    
    // Process inline formatting for regular text
    if (line.trim() !== '') {
      let formattedLine = processInlineFormatting(line);
      for (let i = 0; i < formattedLine.length; i++) commands.push(formattedLine[i]);
      
      const lfCmd = lineFeed();
      addUint8ArrayToNumbers(commands, lfCmd);
    } else {
      // Empty line
      const lfCmd = lineFeed();
      addUint8ArrayToNumbers(commands, lfCmd);
    }
  }
  
  // Add extra space at the end and cut
  const lfCmd = lineFeeds(6);
  addUint8ArrayToNumbers(commands, lfCmd);
  
  const cutCmd = cutPaper();
  addUint8ArrayToNumbers(commands, cutCmd);
  
  return new Uint8Array(commands);
};

// Helper function to add Uint8Array elements to a number array
const addUint8ArrayToNumbers = (target: number[], source: Uint8Array): void => {
  for (let i = 0; i < source.length; i++) {
    target.push(source[i]);
  }
};

// Process inline formatting like bold and italic
const processInlineFormatting = (text: string): number[] => {
  const commands: number[] = [];
  let i = 0;
  
  while (i < text.length) {
    // Bold text
    if (text.substring(i, i + 2) === '**' && text.indexOf('**', i + 2) !== -1) {
      const endBold = text.indexOf('**', i + 2);
      const boldText = text.substring(i + 2, endBold);
      
      const boldCmd = textBold();
      const textCmd = textToUint8Array(boldText);
      const normalCmd = textNormal();
      
      addUint8ArrayToNumbers(commands, boldCmd);
      addUint8ArrayToNumbers(commands, textCmd);
      addUint8ArrayToNumbers(commands, normalCmd);
      
      i = endBold + 2;
      continue;
    }
    
    // Italic text - we'll use underline for italic since most thermal printers don't support italic
    if (text.charAt(i) === '*' && text.indexOf('*', i + 1) !== -1) {
      const endItalic = text.indexOf('*', i + 1);
      const italicText = text.substring(i + 1, endItalic);
      
      // Underline on
      commands.push(0x1B, 0x2D, 0x01);
      
      const textCmd = textToUint8Array(italicText);
      addUint8ArrayToNumbers(commands, textCmd);
      
      // Underline off
      commands.push(0x1B, 0x2D, 0x00);
      
      i = endItalic + 1;
      continue;
    }
    
    // Regular character
    commands.push(text.charCodeAt(i));
    i++;
  }
  
  return commands;
};
